// @ts-nocheck — mixin methods are composed onto BackendService at runtime; instance API is typed via adminBackendServiceInterface.ts.

/**
 * Image Cards and hero image crops: detect and split a screenshot into cards,
 * re-crop a kept source at user-adjusted boxes ("Adjust crops"), and remember
 * each block's crop source so that survives a reload. Uploads go through
 * `prepareExtractionUpload` (content extraction mixin).
 */

export const adminBackendServiceImageCardsMixin = {
  /**
   * Cheap "does this screenshot hold a row/grid of captioned photos?" check —
   * run on a plain image paste into the Image Cards block to decide whether to
   * offer a split. No cropping or upload.
   */
  detectImageCards(file: File): Promise<{ cardCount: number; confidence: string }> {
    return this.authFetchPromise(async (token) => {
      const gcsPath = await this.prepareExtractionUpload(file);
      const response = await this.authenticatedFetch(
        this.apiURL('contentManagement/imageCards/detect'),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            gcsPath,
            mimeType: file.type,
            originalFilename: file.name,
          }),
        },
      );
      if (!response.ok) {
        throw await response
          .json()
          .catch(() => new Error(`Image-card detect failed (${response.status})`));
      }
      const json = await response.json();
      return (json.data ?? json) as { cardCount: number; confidence: string };
    });
  },

  /**
   * "Interpret paste as image cards": a screenshot of a row/grid of captioned
   * photos → a canonical `ImageCarousel` `properties`. The backend detects each
   * photo's bounding box, crops and uploads it, and pairs it with the card's
   * heading/description. The crops are only as sharp as the screenshot.
   */
  extractImageCardsFromScreenshot(
    modelType: string,
    modelId: string,
    file: File,
  ): Promise<{
    properties: any;
    cardCount: number;
    confidence: string;
    source: { url: string; path: string; width: number; height: number } | null;
    boxes: { left: number; top: number; width: number; height: number }[];
  }> {
    return this.authFetchPromise(async (token) => {
      const gcsPath = await this.prepareExtractionUpload(file);
      const response = await this.authenticatedFetch(
        this.apiURL('contentManagement/imageCards/fromScreenshot'),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            gcsPath,
            mimeType: file.type,
            originalFilename: file.name,
            modelType,
            modelId,
          }),
        },
      );
      if (!response.ok) {
        throw await response
          .json()
          .catch(() => new Error(`Image-card split failed (${response.status})`));
      }
      const json = await response.json();
      return json.data ?? json;
    });
  },

  /**
   * Re-crop the retained split source (screenshot / PDF raster) at boxes the
   * user nudged in the adjust dialog. Returns fresh `imageUrl`s to swap into the
   * carousel slides (headings/text stay).
   */
  recropImageCards(
    modelType: string,
    modelId: string,
    sourcePath: string,
    boxes: { left: number; top: number; width: number; height: number }[],
  ): Promise<{
    items: { imageUrl: { id: string; url: string; width?: number; height?: number } }[];
  }> {
    return this.authFetchPromise(async (token) => {
      const response = await this.authenticatedFetch(
        this.apiURL('contentManagement/imageCards/recrop'),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ sourcePath, boxes, modelType, modelId }),
        },
      );
      if (!response.ok) {
        throw await response.json().catch(() => new Error(`Re-crop failed (${response.status})`));
      }
      const json = await response.json();
      return (json.data ?? json) as {
        items: { imageUrl: { id: string; url: string; width?: number; height?: number } }[];
      };
    });
  },

  /**
   * Remember a block's crop source so "Adjust crops" survives a reload —
   * called right after a split/recrop made a block adjustable, once the
   * caller knows which content block the result landed in.
   */
  saveImageCardCropSource(opts: {
    modelType: string;
    modelId: string;
    contentBlockId: string;
    source: { path: string; url: string; width: number; height: number };
    boxes: { left: number; top: number; width: number; height: number }[];
  }): Promise<{ saved: boolean }> {
    return this.authApiMutateAuthed(
      'contentManagement/imageCards/cropSources',
      'POST',
      opts,
      (json: any) => json.data ?? json,
    );
  },

  /**
   * Every still-live crop source for this document's content blocks, keyed
   * by content block id — fetched on load so the editor can offer "Adjust
   * crops" again for any block whose retained source hasn't expired yet.
   */
  listImageCardCropSources(
    modelType: string,
    modelId: string,
  ): Promise<
    Record<
      string,
      {
        sourcePath: string;
        sourceUrl: string;
        sourceWidth: number;
        sourceHeight: number;
        boxes: { left: number; top: number; width: number; height: number }[];
      }
    >
  > {
    const query = new URLSearchParams({ modelType, modelId }).toString();
    return this.authApiGetAuthed(
      `contentManagement/imageCards/cropSources?${query}`,
      'Unable to load crop sources',
      (json: any) => json.data ?? {},
    );
  },
};
